import { useEffect, useRef } from 'react';
import { useEditorStore } from '@/store/editorStore';
import * as fileService from '@/services/file.service';

/* ================================================================== */
/*  Auto-save hook — watches for unsaved changes and persists them    */
/*  Reads settings from localStorage (set by SettingsPanel):          */
/*    ide-autoSave   : boolean (default false)                        */
/*    ide-autoSaveDelay : number ms (default 1000)                    */
/* ================================================================== */

export function useAutoSave(workspaceId: string | null) {
  const { tabs, openFiles, saveFile } = useEditorStore();
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const lastSavedRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    // Read setting from localStorage
    const autoSave = localStorage.getItem('ide-autoSave') === 'true';
    if (!autoSave || !workspaceId) return;

    const delay = parseInt(localStorage.getItem('ide-autoSaveDelay') ?? '1000', 10);

    // Find modified tabs
    const modifiedTabs = tabs.filter((t) => t.isModified);
    if (modifiedTabs.length === 0) return;

    // Clear any pending timer
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      for (const tab of modifiedTabs) {
        const content = openFiles.get(tab.path);
        if (content === undefined) continue;

        // Skip if content hasn't changed since last save
        if (lastSavedRef.current.get(tab.path) === content) continue;

        try {
          await fileService.saveFile(workspaceId, tab.path, content);
          saveFile(tab.path); // mark as saved in store
          lastSavedRef.current.set(tab.path, content);
        } catch (err) {
          console.error(`[auto-save] Failed to save ${tab.path}:`, err);
        }
      }
    }, delay);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [tabs, openFiles, saveFile, workspaceId]);
}
